import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import MaintenancePopup from './components/MaintenancePopup';
import PopupPromo from './components/PopupPromo';

// Páginas cargadas de forma eagerly (landing inicial)
import Home from './pages/Home';
import Login from './pages/Login';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollToTop from './components/ScrollToTop';
import OrganizationSchema from './components/OrganizationSchema';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Páginas lazy — se cargan solo cuando el usuario navega a ellas
const Destinos = lazy(() => import('./pages/Destinos'));
const Contacto = lazy(() => import('./pages/Contacto'));
const Paquetes = lazy(() => import('./pages/Paquetes'));
const PaqueteDetail = lazy(() => import('./pages/PaqueteDetail'));
const Arrepentimiento = lazy(() => import('./pages/Arrepentimiento'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PaginaCMS = lazy(() => import('./pages/PaginaCMS'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ActivarCuenta = lazy(() => import('./pages/ActivarCuenta'));
const Admin = lazy(() => import('./pages/Admin'));
const TravelSale = lazy(() => import('./pages/TravelSale'));
const AdminVouchers = lazy(() => import('./pages/AdminVouchers'));
const TestNuevaAPI = lazy(() => import('./pages/TestNuevaAPI'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-[#2AA8B0] border-t-transparent rounded-full animate-spin" />
  </div>
);

// ✅ Componente wrapper para controlar visibilidad de botones globales
const GlobalButtons = () => {
  const location = useLocation();
  const esPaqueteDetail = location.pathname.startsWith('/paquete/');
  
  if (esPaqueteDetail) {
    return (
      <>
        {/* ScrollToTop global solo en desktop: mobile usa el botón local de PaqueteDetail */}
        <div className="hidden lg:block">
          <ScrollToTop />
        </div>
        <WhatsAppButton />
      </>
    );
  }


  // En otras páginas: mostrar normalmente
  return (
    <>
      <ScrollToTop />
      <WhatsAppButton />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <OrganizationSchema />
        {/*<MaintenancePopup /> */}
          <Router>
            <PopupPromo />
            <GlobalButtons />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ===== RUTAS ESPECÍFICAS (DEBEN IR PRIMERO) ===== */}
              
              {/* Página principal */}
              <Route path="/" element={<Home />} />
              
              {/* Rutas de navegación principales */}
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/activar-cuenta" element={<ActivarCuenta />} />

              <Route path="/destinos" element={<ProtectedRoute><Destinos /></ProtectedRoute>} />
              <Route path="/contacto" element={<ProtectedRoute><Contacto /></ProtectedRoute>} />

              {/* Paquetes - IMPORTANTE: Estas deben ir ANTES de /:slug */}
              <Route path="/paquetes" element={<ProtectedRoute><Paquetes /></ProtectedRoute>} />
              <Route path="/paquetes/:destino" element={<ProtectedRoute><Paquetes /></ProtectedRoute>} />
              <Route path="/paquete/:slug" element={<ProtectedRoute><PaqueteDetail /></ProtectedRoute>} />

              {/* FAQ */}
              <Route path="/preguntas-frecuentes" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
              <Route path="/faq" element={<Navigate to="/preguntas-frecuentes" replace />} />
              <Route path="/FAQ" element={<Navigate to="/preguntas-frecuentes" replace />} />

              <Route path="/arrepentimiento" element={<ProtectedRoute><Arrepentimiento /></ProtectedRoute>} />

              {/* Páginas CMS */}
              <Route path="/paginas/:slug" element={<ProtectedRoute><PaginaCMS /></ProtectedRoute>} />

              {/* TravelSale */}
              <Route path="/travelsale" element={<ProtectedRoute><TravelSale /></ProtectedRoute>} />
              <Route path="/travel-sale/:slug" element={<ProtectedRoute><TravelSale /></ProtectedRoute>} />
              
              {/* Admin - Ruta protegida */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <Admin />
                  </ProtectedRoute>
                } 
              />

              {/* Vouchers - Ruta protegida */}
              <Route 
                path="/admin/vouchers" 
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminVouchers />
                  </ProtectedRoute>
                } 
              />
              
              {/* Test Nueva API - Ruta temporal admin */}
              <Route
                path="/admin/test-nueva-api"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <TestNuevaAPI />
                  </ProtectedRoute>
                }
              />

              {/* ===== RUTA DINÁMICA (DEBE IR AL FINAL) ===== */}
              <Route path="/:slug" element={<ProtectedRoute><TravelSale /></ProtectedRoute>} />
              
              {/* Ruta 404 - Última de todas */}
              <Route path="*" element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                    <p className="text-gray-600">Página no encontrada</p>
                  </div>
                </div>
              } />
            </Routes>
            </Suspense>
          </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;